import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiError } from 'src/app/abstract/api-error';
import { AuthApiService } from 'src/app/core/api-services/auth-api.service';
import { AuthEmailCodeService } from 'src/app/core/api-services/auth-email-code.service';
import { FormInputComponent, FormButtonComponent } from 'src/app/shared/components';
import { NgxOtpInputComponent, OtpStatus } from 'ngx-otp-input';
import { AuthThemeDirective } from 'src/app/core/directives/auth-theme-directive';
import { AuthMethod } from 'src/app/core/data-models/auth-method.model';
import { AuthMode } from 'src/app/core/data-models/auth-mode.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { Auth, ConfirmationResult, getAdditionalUserInfo, linkWithPhoneNumber, PhoneAuthCredential, RecaptchaVerifier, signInWithPhoneNumber, signOut, user } from '@angular/fire/auth';
import { map, Subscription, takeWhile, timer} from 'rxjs';
import { PhoneFormatLibPhonePipe  } from '../../shared/pipes/phone-format-libphone.pipe'
import { BRAND_ASSETS } from 'src/app/core/constants/brand-assets';
import { firebaseErrorMessage } from 'src/app/shared/utils/firebase-error.utils';

interface AuthCodeContent{
  title: string,
  subtitle: string,
  onFormSubmit: () => {},
  /** Runs when all six digits are in — email codes go to the Hub, SMS to Firebase. */
  onCodeComplete: (code: string) => Promise<void>,
  inputForm: FormGroup,
  submitButtonText: string,
}

@Component({
  selector: 'app-auth-code',
  imports: [CommonModule, ReactiveFormsModule, FormInputComponent, FormButtonComponent, RouterLink, NgxOtpInputComponent, AuthThemeDirective, PhoneFormatLibPhonePipe],
  templateUrl: './auth-code.component.html',
  styleUrl: './auth-code.component.scss',
})
export class AuthCodeComponent implements OnInit, OnDestroy {
  readonly logo = BRAND_ASSETS.logoShieldOnDark
  protected readonly AuthMode = AuthMode;
  protected readonly AuthMethod = AuthMethod;
  private onWindowBlur?: () => void;
  onWindowFocus!: () => void;
  private focusTimer?: ReturnType<typeof setTimeout>;
  private redirectTimer?: ReturnType<typeof setTimeout>;

  method!: AuthMethod;
  mode!: AuthMode;
  content!: AuthCodeContent;
  errorMessage: string = '';
  successMessage: string = '';
  step: 'submit' | 'validate' | 'success' = 'submit';
  emailForm: FormGroup;
  phoneForm: FormGroup;
  codeForm: FormGroup;
  loading: boolean = false;
  codeStatus: OtpStatus = 'idle';
  email: string = '';
  phone: string = '';
  oobCode: string = '';
  credential!: PhoneAuthCredential;
  codeValidating: boolean = false;
  codeValidated: boolean = false;
  recaptchaVerifier!: RecaptchaVerifier;
  recaptchaId: string = 'recaptcha-container'
  confirmationResult!: ConfirmationResult;
  codeId: string = '';
  cooldownSeconds = 0;
  next: string = '';
  private cooldownSub?: Subscription;
  
  private readonly contentMap: Record<AuthMode, Partial<Record<AuthMethod, AuthCodeContent>>>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authApiService: AuthApiService,
    private authEmailCodes: AuthEmailCodeService,
    protected authService: AuthService,
    private fb: FormBuilder,
    private router: Router,
    private auth: Auth
  ) {
  this.emailForm = this.fb.nonNullable.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.phoneForm = this.fb.nonNullable.group({
      phone: ['', [Validators.required]]
    })
    this.codeForm = this.fb.nonNullable.group({
      otp: ['', [Validators.required]]
    })

    // Built here rather than as a field initializer: initializers run before
    // the constructor body, so the forms above would still be undefined.
    this.contentMap = {
      [AuthMode.login]:  {
        [AuthMethod.email]: {
          title: 'Login With Email',
          subtitle: `Enter your email address and we'll send a one time code to login`,
          onFormSubmit: () => this.sendEmailCode(),
          onCodeComplete: (code) => this.verifyOtp(code),
          inputForm: this.emailForm,
          submitButtonText: 'Send login code to email'
        },
        [AuthMethod.sms]: {
          title: 'Login With SMS',
          subtitle: `Enter your phone number and we'll send a one time code to login`,
          onFormSubmit: () => this.sendSmsCode(),
          onCodeComplete: (code) => this.verifyFirebaseOtp(code),
          inputForm: this.phoneForm,
          submitButtonText: 'Send login code to phone'
        }
      },
      [AuthMode.verify]: {
        [AuthMethod.email]: {
          title: 'Verify Email',
          subtitle: `Enter the code sent to your email to verify your account`,
          onFormSubmit: () => this.sendEmailCode(),
          onCodeComplete: (code) => this.verifyOtp(code),
          inputForm: this.emailForm,
          submitButtonText: 'Send verification code to email'
        },
        [AuthMethod.sms]: {
          title: 'Verify SMS',
          subtitle: `Enter your phone number and we'll send a one time code to verify it`,
          onFormSubmit: () => this.sendSmsCode(),
          onCodeComplete: (code) => this.verifyFirebaseOtp(code),
          inputForm: this.phoneForm,
          submitButtonText: 'Send verification code to phone'
        }
      },
      [AuthMode.reset]: {
        [AuthMethod.email]: {
          title: 'Reset Password',
          subtitle: `Enter your email address to reset your password`,
          onFormSubmit: () => this.sendEmailCode(),
          onCodeComplete: (code) => this.verifyOtp(code),
          inputForm: this.emailForm,
          submitButtonText: 'Send reset code'
        },
        [AuthMethod.sms]: {
          title: 'Reset Password',
          subtitle: `Enter your phone number to reset your password`,
          onFormSubmit: () => this.sendSmsCode(),
          onCodeComplete: (code) => this.verifyFirebaseOtp(code),
          inputForm: this.phoneForm,
          submitButtonText: 'Send reset code'
        }
      }
    };
    
    //Construct the content based of the snapshot data
    const { mode, method } = this.activatedRoute.snapshot.data;
    const content = this.contentMap[mode as AuthMode]?.[method as AuthMethod];
    if (!content) { this.router.navigate(['/login']); return; }
    this.mode = mode;
    this.method = method; 
    this.content = content;
    
    //Parameters coming into the auth code
    const { id } = this.activatedRoute.snapshot.params;
    const { email, phone, next } = this.router.currentNavigation()?.extras.state ?? history.state;
    this.codeId = id ?? '';
    // Verify pages are always signed in, so guard redirects (which can't carry
    // state) fall back to the signed-in user's own email.
    this.email = email ?? (mode === AuthMode.verify ? this.auth.currentUser?.email ?? '' : '');
    this.phone = phone ?? '';
    this.next = next ?? '';

    // Phone verify lost its state (e.g. refresh); phone changes belong on the profile page.
    if (mode === AuthMode.verify && method === AuthMethod.sms && !this.phone) {
      this.router.navigate(['/dashboard']);
      return;
    }

    // Arriving from a reset link: go straight to the code boxes.
    if (this.codeId) {
      this.step = 'validate';
    }
  }

  //Set up recaptcha, then auto-send on verify pages — the SMS send needs the verifier to exist first.
  ngAfterViewInit(): void {
    this.setRecaptcha();

    const known = this.method === AuthMethod.email ? this.email : this.phone;
    if (this.mode === AuthMode.verify && known) {
      this.content.onFormSubmit();
      this.step = 'validate';
    }
  }

  //Sets up otp for interactivity
  ngOnInit(): void {
    const otp = this.codeForm.get('otp')

    // Blurring though events that need created oninit
    this.onWindowBlur = () => otp?.disable();
    this.onWindowFocus = () => {
      otp?.enable();
      this.focusTimer = setTimeout(() => this.focusOtpInput());
    };
    addEventListener('blur', this.onWindowBlur);
    addEventListener('focus', this.onWindowFocus);

    if (!document.hasFocus()) {
      otp?.disable();
    }
  }

  //Destroys listeners on otp
  ngOnDestroy(): void {
    if (this.onWindowBlur) removeEventListener('blur', this.onWindowBlur);
    if (this.onWindowFocus) removeEventListener('focus', this.onWindowFocus);
    clearTimeout(this.focusTimer);
    clearTimeout(this.redirectTimer);
    this.recaptchaVerifier?.clear();
    this.cooldownSub?.unsubscribe();
  }

  private setRecaptcha(): void {
    //Start recaptcha for phone login
    if (this.method === AuthMethod.sms) {
      this.recaptchaVerifier = new RecaptchaVerifier(this.auth, this.recaptchaId, {
        'size': 'invisible',
        'callback': (_: any) => {}
      })
    }
  }

  private focusOtpInput(): void {
    document.getElementById('otp')?.querySelector('input')?.focus();
  }


  getErrorMessage(controlName: string): string {
    // The active form holds whichever field is on screen (email or phone).
    const control = this.content.inputForm.get(controlName);
    if (!control || !control.touched || !control.invalid) return '';
    if (control.hasError('required')) return `${this.formatFieldName(controlName)} is required`;
    if (control.hasError('email')) return 'Please enter a valid email address';
    if (control.hasError('phone')) return 'Please enter a valid phone number';
    return 'Invalid value';
  }

  private formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
  
  private startCooldown(): void {
    const endsAt = Date.now() + 60_000;
    this.cooldownSub?.unsubscribe();
    this.cooldownSub = timer(0, 1000).pipe(
      map(() => Math.ceil((endsAt - Date.now()) / 1000)),
      takeWhile(seconds => seconds > 0, true),
    ).subscribe(seconds => this.cooldownSeconds = seconds);
  }

  //Sends the code (with the correct mode) to email
  async sendEmailCode(): Promise<void> {
    // Verify always uses the account's email; resends reuse the one already sent to.
    if (this.step === 'submit' && this.mode !== AuthMode.verify) {
      if (this.emailForm.invalid) return;
      this.email = this.emailForm.value.email
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      if (this.mode === AuthMode.reset) {
        await this.authEmailCodes.sendResetPassword(this.email);
      }
      if (this.mode === AuthMode.login) {
        await this.authEmailCodes.sendEmailLoginCode(this.email);
      }
      if (this.mode === AuthMode.verify) {
        await this.authEmailCodes.resendVerificationEmail();
      }
      this.step = 'validate';
      this.startCooldown();
    } catch (error: unknown) {
      const status = ApiError.fromHttpError(error).status;
      this.errorMessage = status === 403 || status === 429
        ? 'A code was sent recently. Please wait a minute before requesting another.'
        : 'Unable to send a code. Please try again.';
      return;
    } finally {
      this.loading = false;
    }
  }

  //Send Firebase sms code for login
  async sendSmsCode(): Promise<void> {
    if (this.step === 'submit' && this.mode !== AuthMode.verify) {
      if (this.phoneForm.invalid) return;
      this.phone = this.phoneForm.value.phone;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      // Verify attaches the phone to the signed-in account; login and reset sign in by phone.
      this.confirmationResult = this.mode === AuthMode.verify
        ? await linkWithPhoneNumber(this.auth.currentUser!, this.phone, this.recaptchaVerifier)
        : await signInWithPhoneNumber(this.auth, this.phone, this.recaptchaVerifier);
      this.step = 'validate';
      this.onWindowFocus();
    } catch (error) {
      console.error('[AuthCode] SMS send failed:', error);
      this.errorMessage = firebaseErrorMessage(error);
      // The reCAPTCHA token was spent on the failed attempt; a retry needs a fresh one.
      this.recaptchaVerifier?.clear();
      this.setRecaptcha();
    } finally {
      this.loading = false;
    }
  }


  async resendCode(): Promise<void> {
    this.loading = true;
    try {
      await this.content.onFormSubmit();
      if (!this.errorMessage) {
        this.successMessage = 'New Code Sent';
      }
    } catch {
      console.log(`Couldn't Resend`)
    } finally {
      this.loading = false;
    }
  }


  // Reverts OTP input back to idle
  changeStatus(event: {value: string, isComplete: boolean}) {
    if (!event.isComplete) {
      this.codeStatus = 'idle'
    }
  }

  // Sends user back to input page
  backToInput() {
    clearTimeout(this.redirectTimer);
    // A link's code id belongs to the old address; the new code is checked by email.
    this.codeId = '';
    // Cleared so the next send reads the newly typed address instead of reusing the old one.
    this.email = '';
    this.phone = '';
    this.step = 'submit';
    this.emailForm.reset();
    this.phoneForm.reset();
    this.successMessage = '';
    this.errorMessage = '';
  }

  // Verifies the SMS OTP Firebase sends
  async verifyFirebaseOtp(code: string) {
    this.codeStatus = 'idle';
    this.codeValidating = true;
  
    try {
      //Confirm phone code and LOGS USER IN
      const userCred = await this.confirmationResult.confirm(code);

      //Check if the phone is linked to a user in the db,
      //If not, delete orphan firebase user
      if (getAdditionalUserInfo(userCred)?.isNewUser) {
        await userCred.user.delete();
        this.codeForm.get('otp')?.reset('');
        this.codeStatus = 'error';
        this.successMessage = '';
        this.errorMessage = 'No account is linked to this phone number. Sign in with your email instead.';
        return;
      }

      if (this.mode === AuthMode.reset) {
        if (!userCred.user.email) {
          await signOut(this.auth);
          this.codeForm.get('otp')?.reset('');
          this.codeStatus = 'error';
          this.successMessage = '';
          this.errorMessage = 'No email is associated with this account. Required for reset by phone number'
          return;
        }

        // Fetched while still signed in — the Hub needs the phone sign-in token.
        // Signed out either way so a failed request doesn't leave the session open.
        this.email = userCred.user.email;
        try {
          this.oobCode = (await this.authApiService.getResetActionCode()).oobCode;
        } finally {
          await signOut(this.auth);
        }
      } else if (this.mode === AuthMode.login) {
        this.authApiService.recordLogin().catch(err => console.error('Failed to record login:', err));
      }

      this.verifyOtpSuccess();
    } catch (error) {
      this.verifyOtpFailure(error);
    } finally {
      this.verifyOtpFinally();
    }
  }

  async verifyOtp(code: string) {
    this.codeStatus = 'idle';
    this.codeValidating = true;
    
    try {
      // Email OTP oobcode verification
      if (!this.email && !this.codeId) {
        this.errorMessage = `We lost track of your email address. Please try again`;
        this.successMessage = '';
        return;
      }

      // in verifyOtp:
      const response = await this.requestCodeVerification(code);
      
      // On Failure
      if (!response.oobCode) {
        this.codeStatus = 'error';
        this.errorMessage = 'Something went wrong. Please try again.';
        this.successMessage = '';
        return;
      }
      
      // Both are needed by onSubmit: the oobCode to complete the Firebase
      // reset, the email to sign in afterwards.
      this.oobCode = response.oobCode;

      if (!this.email && response.email) {
        this.email = response.email
      }
      
      this.verifyOtpSuccess();
    } catch (error: unknown) {
      this.verifyOtpFailure(error)
    } finally {
      this.verifyOtpFinally();
    }
  }

  // Either verify with email + code or code id + code
  private requestCodeVerification(code: string) {
    return this.codeId
      ? this.authEmailCodes.verifyResetCodeWithId(this.codeId, code)
      : this.authEmailCodes.verifyAuthEmailCode(this.email, code, this.mode);
  }
  
  verifyOtpSuccess(): void {
    //Success state
    this.codeStatus = 'success';
    this.successMessage = 'Successfully validated code';
    this.errorMessage = '';
    this.codeValidated = true;
    
    this.navigateAfterSuccess();
  }

  verifyOtpFailure(error: unknown): void {
    this.codeForm.get('otp')?.reset('');
    this.codeStatus = 'error';
    // Hub failures arrive as ApiError (message = the hub's error); Firebase ones (SMS) carry a `code`.
    this.errorMessage = error instanceof ApiError
      ? (error.errors[0] ?? 'Something went wrong. Please try again.')
      : firebaseErrorMessage(error);
    this.successMessage = '';
  }

  verifyOtpFinally(): void {
    // Only refocus while the code step is still on screen — once the password
    // form renders, the first input is the password field.
    if (!this.codeValidated) {
      this.focusOtpInput();
    }
    this.codeValidating = false;
  }

  async navigateAfterSuccess() {
    let path = '/success';
    let state: any = { mode: this.mode, method: this.method }

    //Reset path sends user to reset password page
    if (this.mode === AuthMode.reset) {
      path = '/reset-password';
      state = { oobCode: this.oobCode, email: this.email };
    }

    //Login path logs in user if oobCode option
    //Sms verify otp also logs in at time of verification
    if (this.mode === AuthMode.login && this.oobCode) {
      try {
        await this.authService.loginWithCode(this.oobCode, this.email);
      } catch (error) {
        this.codeValidated = false;
        this.verifyOtpFailure(error);
        return;
      }
    }

    // Email verify: the code only proves the inbox — applying it marks the account verified.
    if (this.mode === AuthMode.verify && this.method === AuthMethod.email) {
      try {
        await this.authService.verifyEmail(this.oobCode);
      } catch (error) {
        this.codeValidated = false;
        this.verifyOtpFailure(error);
        return;
      }
    }

    // A chained step (e.g. email then phone) gets the contact details along with it.
    if (this.next) {
      path = this.next;
      state = { email: this.email, phone: this.phone, verified: true };
    }

    //Both reset and login go to success page
    this.redirectTimer = setTimeout(() => {
      this.router.navigate([path], { state: state })
    }, 1500)
  }

}
